// src/modules/app-views/dataSearch/pages/Search/components/ChatMode.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Card, Typography, theme } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import { BsRobot } from 'react-icons/bs';
import useZustand from 'hooks/useZustand';
import SimpleAvatar from 'components/SimpleComponents/SimpleAvatar';
import translateX from 'utils/translateX';
import InputForm from './InputForm';
import SearchedEntity from './SearchedEntity';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * ChatMode - Chat interface with messages for data search
 */
const ChatMode = ({
  messages,
  formRef,
  isProcessing,
  lastFormValues,
  selectedEngine,
  selectedSearchType,
  searchEngines,
  searchTypeOptions,
  creditsInfo,
  elapsedSeconds,
  typingProgress,
  introTypingComplete,
  searchedEntityId,
  searchDataAge,
  chatContainerRef,
  messagesEndRef,
  isMobile,
  onEngineChange,
  onSearchTypeChange,
  onFinish,
  onBackToWelcome,
  onIntroComplete,
  onUpdateEntity,
  onCreateEntity,
  onOpenCreditsDrawer,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const storedUser = useZustand('auth', 'user');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        ...(isMobile ? {} : { height: '100%' }),
      }}
    >
      {/* Messages Container */}
      <div
        ref={chatContainerRef}
        style={{
          flex: isMobile ? undefined : 1,
          overflowY: 'auto',
          paddingTop: 24,
          paddingBottom: 24,
          border: `1px solid ${token.colorPrimary}40`,
          borderRadius: 8,
          // Mobile: fixed height so chat has its own scroll (not page scroll)
          // Height = viewport - header(70) - pageHeader(100) - cardPadding(26) - inputForm(200) - margins(50)
          ...(isMobile && { height: 'calc(100svh - 446px)', minHeight: 300 }),
        }}
      >
        <div
          style={{
            maxWidth: 900,
            margin: '0 auto',
            padding: '0 16px',
          }}
        >
          {messages.map((msg, idx) =>
            renderMessage(msg, idx, {
              token,
              storedUser,
              elapsedSeconds,
              typingProgress,
              introTypingComplete,
              chatContainerRef,
              creditsInfo,
              searchDataAge,
              isMobile,
              onIntroComplete,
              onUpdateEntity,
              onCreateEntity,
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Container */}
      <div>
        <div
          style={{
            padding: '24px 0 12px',
          }}
        >
          <InputForm
            formRef={formRef}
            isChatMode={true}
            isProcessing={isProcessing}
            lastFormValues={lastFormValues}
            selectedEngine={selectedEngine}
            selectedSearchType={selectedSearchType}
            searchEngines={searchEngines}
            searchTypeOptions={searchTypeOptions}
            creditsInfo={creditsInfo}
            onEngineChange={onEngineChange}
            onSearchTypeChange={onSearchTypeChange}
            onFinish={onFinish}
            onBackToWelcome={onBackToWelcome}
            onOpenCreditsDrawer={onOpenCreditsDrawer}
          />
        </div>
      </div>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ChatMode;

// *******************************************************************************
// HELPERS
// *******************************************************************************
/**
 * Render a single message (user or assistant)
 */
function renderMessage(message, index, context) {
  const {
    token,
    storedUser,
    elapsedSeconds,
    typingProgress,
    introTypingComplete,
    chatContainerRef,
    creditsInfo,
    searchDataAge,
    isMobile,
    onIntroComplete,
    onUpdateEntity,
    onCreateEntity,
  } = context;

  const isUser = message.role === 'user';
  const isThinking = message.status === 'thinking';

  // Mobile layout: vertical with centered avatar
  if (isMobile) {
    return (
      <div
        key={index}
        style={{
          marginBottom: 24,
        }}
      >
        {/* Avatar centered */}
        <Flex justify="center" style={{ marginBottom: 8 }}>
          {isUser ? (
            <SimpleAvatar
              src={
                storedUser?.avatar?.base64Thumb ||
                storedUser?.avatar?.thumbUrl ||
                storedUser?.avatar?.storageUrl
              }
              name={storedUser?.name}
              size={36}
              shape="circle"
              backColor={storedUser?.backColor}
              foreColor={storedUser?.foreColor}
            />
          ) : (
            <Flex
              align="center"
              justify="center"
              className={isThinking ? 'ai-icon-thinking' : ''}
              style={{
                width: 36,
                height: 36,
                backgroundColor: token.colorPrimary,
                borderRadius: '50%',
              }}
            >
              <BsRobot style={{ color: '#fff', fontSize: 20 }} />
            </Flex>
          )}
        </Flex>

        {/* Content below avatar - full width */}
        {!isUser && isThinking ? (
          <Flex justify="center">
            {renderAssistantMessage(message, {
              elapsedSeconds,
              typingProgress,
              introTypingComplete,
              chatContainerRef,
              creditsInfo,
              searchDataAge,
              onIntroComplete,
              onUpdateEntity,
              onCreateEntity,
            })}
          </Flex>
        ) : (
          <Card
            variant={isUser ? 'outlined' : 'borderless'}
            className="no-shadow"
            size="small"
            styles={
              !isUser && {
                body: {
                  padding: '12px 0',
                },
              }
            }
            style={{
              borderRadius: 12,
              width: '100%',
            }}
          >
            {isUser
              ? renderUserMessage(message)
              : renderAssistantMessage(message, {
                  elapsedSeconds,
                  typingProgress,
                  introTypingComplete,
                  chatContainerRef,
                  creditsInfo,
                  searchDataAge,
                  onIntroComplete,
                  onUpdateEntity,
                  onCreateEntity,
                })}
          </Card>
        )}
      </div>
    );
  }

  // Desktop layout: horizontal with avatar on side
  return (
    <div
      key={index}
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        marginBottom: 24,
        paddingLeft: isUser ? 60 : 0,
        paddingRight: isUser ? 0 : 60,
      }}
    >
      <Flex gap={12} align="flex-start" style={{ maxWidth: '100%' }}>
        {!isUser && (
          <Flex
            align="center"
            justify="center"
            className={isThinking ? 'ai-icon-thinking' : ''}
            style={{
              width: 36,
              height: 36,
              flexShrink: 0,
              backgroundColor: token.colorPrimary,
              borderRadius: '50%',
            }}
          >
            <BsRobot style={{ color: '#fff', fontSize: 20 }} />
          </Flex>
        )}

        {/* Thinking status: simple text without card */}
        {!isUser && isThinking ? (
          <Flex align="center" style={{ minHeight: 36, paddingLeft: 16 }}>
            {renderAssistantMessage(message, {
              elapsedSeconds,
              typingProgress,
              introTypingComplete,
              chatContainerRef,
              creditsInfo,
              searchDataAge,
              onIntroComplete,
              onUpdateEntity,
              onCreateEntity,
            })}
          </Flex>
        ) : (
          <Card
            variant={isUser ? 'outlined' : 'borderless'}
            className="no-shadow"
            size="small"
            styles={{
              body: {
                padding: '12px 16px',
              },
            }}
            style={{
              borderRadius: 12,
              maxWidth: '100%',
              width: !isUser ? '100%' : 'auto',
            }}
          >
            {isUser
              ? renderUserMessage(message)
              : renderAssistantMessage(message, {
                  elapsedSeconds,
                  typingProgress,
                  introTypingComplete,
                  chatContainerRef,
                  creditsInfo,
                  searchDataAge,
                  onIntroComplete,
                  onUpdateEntity,
                  onCreateEntity,
                })}
          </Card>
        )}

        {isUser && (
          <SimpleAvatar
            src={
              storedUser?.avatar?.base64Thumb ||
              storedUser?.avatar?.thumbUrl ||
              storedUser?.avatar?.storageUrl
            }
            name={storedUser?.name}
            size={36}
            shape="circle"
            backColor={storedUser?.backColor}
            foreColor={storedUser?.foreColor}
            style={{ flexShrink: 0 }}
          />
        )}
      </Flex>
    </div>
  );
}

/**
 * Render user message content
 */
function renderUserMessage(message) {
  return (
    <div>
      <div style={{ fontWeight: 500, marginBottom: 4 }}>{translateX('search_data')}:</div>
      <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>
    </div>
  );
}

/**
 * Render assistant message content based on status
 */
function renderAssistantMessage(message, context) {
  const {
    elapsedSeconds,
    typingProgress,
    introTypingComplete,
    chatContainerRef,
    creditsInfo,
    searchDataAge,
    onIntroComplete,
    onUpdateEntity,
    onCreateEntity,
  } = context;

  if (message.status === 'thinking') {
    return (
      <span style={{ fontSize: 15, lineHeight: 1.6, color: 'var(--ant-color-text)' }}>
        {translateX('searching_data')}... {translateX('wait')}... {elapsedSeconds}s
      </span>
    );
  }

  if (message.status === 'error') {
    return renderErrorMessage(message);
  }

  if (message.status === 'success' && message.entity) {
    return (
      <SearchedEntity
        message={message}
        typingProgress={typingProgress}
        introTypingComplete={introTypingComplete}
        chatContainerRef={chatContainerRef}
        creditsInfo={creditsInfo}
        searchDataAge={searchDataAge}
        onIntroComplete={onIntroComplete}
        onUpdateEntity={onUpdateEntity}
        onCreateEntity={onCreateEntity}
      />
    );
  }

  return <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>;
}

/**
 * Render error message with details
 */
function renderErrorMessage(message) {
  const { errorDetails } = message;

  // Translate keys at render time (allows language changes to take effect)
  const explanation = errorDetails?.explanationKey
    ? translateX(errorDetails.explanationKey)
    : errorDetails?.explanationFallback || translateX('unexpected_error_occurred');

  const suggestions = (errorDetails?.suggestionKeys || []).map(key => translateX(key));

  return (
    <div style={{ width: '100%' }}>
      {/* Error header */}
      <Flex gap={8} align="center" style={{ marginBottom: 12 }}>
        <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
        <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
          {translateX('error_searching_data')}
        </span>
      </Flex>

      {/* Error explanation */}
      <div
        style={{
          padding: 16,
          backgroundColor: 'var(--ant-color-error-bg)',
          borderRadius: 8,
          border: '1px solid var(--ant-color-error-border)',
        }}
      >
        {/* Main error message */}
        <div style={{ marginBottom: 12 }}>
          <Text strong>{translateX('what_happened')}:</Text>
          <div style={{ marginTop: 4 }}>{explanation}</div>
        </div>

        {/* Technical details (if available) */}
        {errorDetails?.technicalMessage && (
          <div style={{ marginBottom: 12 }}>
            <Text strong>{translateX('technical_details')}:</Text>
            <div
              style={{
                marginTop: 4,
                fontFamily: 'monospace',
                fontSize: 12,
                color: 'var(--ant-color-text-secondary)',
              }}
            >
              {errorDetails.technicalMessage}
            </div>
          </div>
        )}

        {/* Suggestions */}
        {suggestions.length > 0 && (
          <div>
            <Text strong>{translateX('what_you_can_do')}:</Text>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
              {suggestions.map((suggestion, idx) => (
                <li key={idx} style={{ marginBottom: 4 }}>
                  {suggestion}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Try again hint */}
      <div style={{ marginTop: 12 }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {translateX('try_again_hint')}
        </Text>
      </div>
    </div>
  );
}
